import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import {
  CONTACT_FIELDS,
  MATCH_NOTE_FIELD,
  OVERVIEW_FIELDS,
  displayValue,
  formatDateTime,
  getCoverPhoto,
  parsePhotoUrls,
  type MatchProfile,
} from "../adminConfig";
import MemberForm from "./MemberForm";
import PhotoLightbox from "./PhotoLightbox";

interface MemberDetailDrawerProps {
  profile: MatchProfile | null;
  canEdit: boolean;
  canDelete?: boolean;
  onClose: () => void;
  onUpdated: (profile: MatchProfile) => void;
  onDelete?: (profile: MatchProfile) => void;
}

function avatarClass(gender: string | null): string {
  if (gender === "男") return "bg-secondary-100 text-secondary-700";
  if (gender === "女") return "bg-accent-100 text-accent-700";
  return "bg-background-200 text-foreground-600";
}

export default function MemberDetailDrawer({
  profile,
  canEdit,
  canDelete = false,
  onClose,
  onUpdated,
  onDelete,
}: MemberDetailDrawerProps) {
  const [copied, setCopied] = useState("");
  const [editing, setEditing] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    if (profile) {
      setCopied("");
      setEditing(false);
      setLightboxIndex(null);
    }
  }, [profile]);

  if (!profile) return null;

  const photos = parsePhotoUrls(profile.photo_url);
  const cover = getCoverPhoto(profile.photo_url);

  const handleCopy = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      window.setTimeout(() => setCopied(""), 1600);
    } catch {
      setCopied("");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-foreground-950/40" onClick={onClose}></div>

      <div className="relative flex h-full w-full max-w-xl flex-col border-l border-background-200 bg-background-100">
        <div className="flex items-start justify-between gap-4 border-b border-background-200 bg-background-50 px-5 py-4">
          <div className="flex min-w-0 items-center gap-3">
            {cover ? (
              <button
                type="button"
                onClick={() => setLightboxIndex(0)}
                className="h-11 w-11 shrink-0 cursor-zoom-in overflow-hidden rounded-full"
                aria-label="查看大图"
              >
                <img
                  src={cover}
                  alt={profile.name ? `${profile.name}的照片` : "会员照片"}
                  className="h-full w-full object-cover object-top"
                />
              </button>
            ) : (
              <span
                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-base font-medium ${avatarClass(
                  profile.gender,
                )}`}
              >
                {profile.name ? profile.name.slice(0, 1) : "?"}
              </span>
            )}
            <div className="min-w-0">
              <p className="truncate font-heading text-base font-semibold text-foreground-950">
                {profile.name || "未填写姓名"}
              </p>
              <p className="mt-0.5 truncate text-xs text-foreground-400">
                {profile.gender || "—"} · {profile.age ? `${profile.age} 岁` : "年龄未填"} · 提交于{" "}
                {formatDateTime(profile.created_at)}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {canEdit && !editing && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-edit-line"></i>
                编辑资料
              </button>
            )}
            {canDelete && !editing && onDelete && (
              <button
                type="button"
                onClick={() => onDelete(profile)}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:border-accent-300 hover:text-accent-600"
              >
                <i className="ri-delete-bin-line"></i>
                删除资料
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-900"
              aria-label="关闭"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-5">
          {editing ? (
            <MemberForm
              key={profile.id}
              initial={profile}
              submitLabel="保存资料"
              cancelLabel="取消"
              onCancel={() => setEditing(false)}
              onSubmit={async (payload) => {
                const { data, error: updateError } = await supabase
                  .from("match_profiles")
                  .update(payload)
                  .eq("id", profile.id)
                  .select("*")
                  .maybeSingle();
                if (updateError) throw updateError;
                onUpdated(
                  (data as MatchProfile) || { ...profile, ...(payload as Partial<MatchProfile>) },
                );
              }}
            />
          ) : (
            <>
          {photos.length > 0 && (
            <section className="rounded-lg border border-background-200 bg-background-50">
              <div className="flex items-center gap-2 border-b border-background-200 px-4 py-3">
                <span className="flex h-7 w-7 items-center justify-center rounded-md bg-accent-100 text-accent-600">
                  <i className="ri-image-2-line text-sm"></i>
                </span>
                <h3 className="font-heading text-sm font-semibold text-foreground-900">会员照片</h3>
                <span className="text-xs text-foreground-400">（共 {photos.length} 张，点击看大图）</span>
              </div>
              <div className="grid grid-cols-3 gap-3 px-4 py-4 sm:grid-cols-4">
                {photos.map((url, index) => (
                  <button
                    key={`${url}-${index}`}
                    type="button"
                    onClick={() => setLightboxIndex(index)}
                    className="relative aspect-[3/4] cursor-zoom-in overflow-hidden rounded-lg border border-background-200 bg-background-100"
                  >
                    <img
                      src={url}
                      alt={`${profile.name || "会员"}的照片 ${index + 1}`}
                      className="h-full w-full object-cover object-top"
                    />
                  </button>
                ))}
              </div>
            </section>
          )}

          <section className="rounded-lg border border-background-200 bg-background-50">
            <div className="flex items-center gap-2 border-b border-background-200 px-4 py-3">
              <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                <i className="ri-contacts-line text-sm"></i>
              </span>
              <h3 className="font-heading text-sm font-semibold text-foreground-900">联系方式</h3>
            </div>
            <div className="grid grid-cols-1 gap-3 px-4 py-4 sm:grid-cols-2">
              {CONTACT_FIELDS.map((field) => {
                const raw = profile[field.key];
                const text = raw ? String(raw) : "";
                return (
                  <div
                    key={field.key}
                    className="rounded-md border border-background-200 bg-background-50 px-3 py-2.5"
                  >
                    <p className="text-xs text-foreground-400">{field.label}</p>
                    <div className="mt-1 flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-medium text-foreground-900">
                        {text || "—"}
                      </span>
                      {text && (
                        <button
                          type="button"
                          onClick={() => handleCopy(text, field.key)}
                          className="shrink-0 cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-2 py-0.5 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
                        >
                          {copied === field.key ? "已复制" : "复制"}
                        </button>
                      )}
                    </div>
                    {field.key === "phone" && !canEdit && text && (
                      <p className="mt-1 text-xs text-foreground-400">手机号为脱敏显示，完整号码仅管理员可见</p>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          <section className="rounded-lg border border-background-200 bg-background-50">
            <div className="flex items-center gap-2 border-b border-background-200 px-4 py-3">
              <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                <i className="ri-file-list-3-line text-sm"></i>
              </span>
              <h3 className="font-heading text-sm font-semibold text-foreground-900">资料一览</h3>
              <span className="text-xs text-foreground-400">（可直接截图发送）</span>
            </div>
            <div className="grid grid-cols-1 gap-x-5 gap-y-4 px-4 py-4 sm:grid-cols-2">
              {OVERVIEW_FIELDS.map((field) => (
                <div key={field.key} className={field.full ? "sm:col-span-2" : ""}>
                  <p className="text-xs text-foreground-400">{field.label}</p>
                  <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground-900">
                    {displayValue(profile[field.key], field.suffix)}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-lg border border-background-200 bg-background-50">
            <div className="flex items-center gap-2 border-b border-background-200 px-4 py-3">
              <span className="flex h-7 w-7 items-center justify-center rounded-md bg-accent-100 text-accent-600">
                <i className="ri-heart-2-line text-sm"></i>
              </span>
              <h3 className="font-heading text-sm font-semibold text-foreground-900">匹配记录</h3>
            </div>
            <div className="px-4 py-4">
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground-900">
                {displayValue(profile[MATCH_NOTE_FIELD.key])}
              </p>
            </div>
          </section>
            </>
          )}
        </div>
      </div>

      {lightboxIndex !== null && (
        <PhotoLightbox
          images={photos}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
        />
      )}
    </div>
  );
}